import { Injectable } from '@angular/core';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Action, NgxsAfterBootstrap, Selector, State, StateContext, Store } from '@ngxs/store';
import { from, of, switchMap } from 'rxjs';
import { AppFeaturesState } from '../portal/app-feature.state';
import { ResetState } from '../shared';
import { FirebaseService } from './firebase.service';
import { AlertController } from '@ionic/angular';
import { AppLauncher } from '@capacitor/app-launcher';
import { AppFeaturesService } from '../portal/app-features.service';
import { AndroidSettings, NativeSettings } from 'capacitor-native-settings';
import { UserRegisterDevice } from '../portal/userDetails.state';

export class FireBaseInitialize {
  static readonly type = '@firebaseState.initialize';
}

export class FireBaseCheckPermission {
  static readonly type = '@firebaseState.checkPermissions';
  constructor(public skipPrompt?: boolean) {}
}

export class FireBaseNotificationAlertInQueue {
  static readonly type = '@firebaseState.patchNotificationAlertInQueue';
  constructor(public inQueue: boolean) {}
}

interface FirebaseStateModel {
  deviceToken: string | undefined;
  initialized: boolean;
  isNotificationAlertQueued: boolean;
}

@State<FirebaseStateModel>({
  name: 'firebaseState',
  defaults: {
    deviceToken: undefined,
    initialized: false,
    isNotificationAlertQueued: false,
  },
})
@Injectable()
export class FirebaseState implements NgxsAfterBootstrap {
  constructor(
    private firebaseService: FirebaseService,
    private appFeaturesState: AppFeaturesState,
    private alertController: AlertController,
    private store: Store,
    private appFeaturesService: AppFeaturesService
  ) {}

  @Selector()
  static deviceToken(state: FirebaseStateModel) {
    return state.deviceToken;
  }

  @Selector()
  static isNotificationAlertQueued(state: FirebaseStateModel) {
    return state.isNotificationAlertQueued;
  }

  ngxsAfterBootstrap(ctx?: StateContext<FirebaseStateModel> | undefined): void {
    const isApp = this.appFeaturesState.isNativeIos || this.appFeaturesState.isNativeAndroid;
    if (!isApp) {
      // this.firebaseService
      //   .requestPermission()
      //   .pipe(
      //     tap((deviceToken) => {
      //       if (ctx) {
      //         ctx.patchState({ deviceToken });
      //         this.firebaseService.listen();
      //       }
      //     })
      //   )
      //   .subscribe();
    } else {
      PushNotifications.addListener('registration', (token: Token) => {
        console.log('Push registration success, token: ' + token.value);
        ctx?.patchState({ deviceToken: token.value });
      });
      ctx?.dispatch(new FireBaseCheckPermission());
    }
  }

  @Action(FireBaseCheckPermission)
  checkPermission(ctx: StateContext<FirebaseStateModel>, action: FireBaseCheckPermission) {
    const isApp = this.appFeaturesState.isNativeIos || this.appFeaturesState.isNativeAndroid;
    if (!isApp) {
      return;
    }
    const permissionStatus$ = from(PushNotifications.requestPermissions());
    return permissionStatus$.pipe(
      switchMap((pushNotificationPermissionState) => {
        if (pushNotificationPermissionState.receive !== 'granted') {
          console.log('notificationState', pushNotificationPermissionState.receive);
          if (action.skipPrompt) {
            return of(true);
          }
          return from(this.#dialogToEnableNotification());
        }
        return from(PushNotifications.register()).pipe(switchMap(() => this.store.dispatch(new UserRegisterDevice())));
      })
    );
  }

  async #dialogToEnableNotification() {
    if (this.store.selectSnapshot(FirebaseState.isNotificationAlertQueued)) {
      return;
    }
    this.store.dispatch(new FireBaseNotificationAlertInQueue(true));
    const requestNotificationAlert = await this.alertController.create({
      header: 'Notifications Disabled',
      message: 'Notifications are turned off. Tap below to open your phone’s settings and re-enable them for AllRotors.',
      cssClass: 'enable-notification-alert',
      backdropDismiss: false,

      buttons: [
        {
          cssClass: 'cancel',
          text: 'Maybe Later',
          role: 'close',
        },
        {
          cssClass: 'proceed',
          role: 'proceed',
          text: 'Open Settings',
          handler: () => {
            if (this.appFeaturesService.isNativeIos) {
              return AppLauncher.openUrl({ url: 'app-settings:' });
            }
            if (this.appFeaturesService.isNativeAndroid) {
              return NativeSettings.openAndroid({
                option: AndroidSettings.AppNotification,
              });
            }
            return undefined;
          },
        },
      ],
    });
    await requestNotificationAlert.present();
    await requestNotificationAlert.onDidDismiss();
    this.store.dispatch(new FireBaseNotificationAlertInQueue(false));
  }

  @Action(FireBaseNotificationAlertInQueue)
  patchNotificationAlertInQueue(ctx: StateContext<FirebaseStateModel>, action: FireBaseNotificationAlertInQueue) {
    ctx.patchState({
      isNotificationAlertQueued: action.inQueue,
    });
  }

  @Action(ResetState)
  resetState(ctx: StateContext<FirebaseStateModel>, action: ResetState) {
    ctx.setState({
      deviceToken: undefined,
      initialized: false,
      isNotificationAlertQueued: false,
    });
  }

  @Action(FireBaseInitialize)
  initializeFirebase(ctx: StateContext<FirebaseStateModel>) {
    const state = ctx.getState();
    if (state.initialized) {
      return;
    }
    this.firebaseService.initializeFirebase();
    ctx.patchState({
      initialized: true,
    });
  }
}
